/**
 * Multiple-choice quiz: setup, one question at a time, then the marked result.
 *
 * Per-question timing is measured with performance.now() rather than Date.now()
 * because the backend awards "easy" to answers under four seconds, and a wall
 * clock adjustment mid-quiz would silently distort that.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiError, LEVELS, api, levelColor, type Level, type QuizMode } from '../api'
import type { Quiz as QuizSession, QuizResult } from '../api'
import QuizReview from '../components/QuizReview'
import { ErrorNote, LevelBadge, LevelPicker, Progressbar, Spinner } from '../components/ui'
import { useAuth } from '../store/auth'

interface Answer {
  position: number
  answer: string
  elapsed_ms: number
}

type Phase = 'setup' | 'running' | 'done'

/** The API rejects anything above 30 minutes (schemas.AnswerIn.elapsed_ms). */
const MAX_ELAPSED_MS = 30 * 60 * 1000

function clampElapsed(ms: number): number {
  if (!Number.isFinite(ms) || ms < 0) return 0
  return Math.min(MAX_ELAPSED_MS, Math.round(ms))
}

const MODES: { value: QuizMode; label: string; blurb: string }[] = [
  { value: 'mixed', label: 'Mixed', blurb: 'A shuffle of all four question types.' },
  { value: 'meaning', label: 'Meaning', blurb: 'See the kanji, pick the English meaning.' },
  { value: 'reading', label: 'Reading', blurb: 'See the kanji, pick the romaji reading.' },
  { value: 'component', label: 'Components', blurb: 'See the kanji, pick a part it is built from.' },
  { value: 'kanji_from_meaning', label: 'Meaning → kanji', blurb: 'See an English meaning, pick the kanji.' },
]

const COUNTS = [5, 10, 20]

/** Modes whose answer text is Japanese, so options need the JP font stack. */
const JP_ANSWER_MODES = ['component', 'kanji_from_meaning']

function asLevel(value: string | null): Level | null {
  return value !== null && (LEVELS as string[]).includes(value) ? (value as Level) : null
}

function errText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Could not reach the server.'
}

function clock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

export default function Quiz() {
  const { user } = useAuth()
  const [params] = useSearchParams()

  const [phase, setPhase] = useState<Phase>('setup')
  const [level, setLevel] = useState<Level>(asLevel(params.get('level')) ?? user?.target_level ?? 'N5')
  const [mode, setMode] = useState<QuizMode>('mixed')
  const [count, setCount] = useState(10)
  const [onlyDue, setOnlyDue] = useState(false)

  const [quiz, setQuiz] = useState<QuizSession | null>(null)
  const [position, setPosition] = useState(0)
  const [answers, setAnswers] = useState<Answer[]>([])
  const [result, setResult] = useState<QuizResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const quizStart = useRef(0)
  const questionStart = useRef(0)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (phase !== 'running') return
    const id = window.setInterval(() => setTick(performance.now()), 250)
    return () => window.clearInterval(id)
  }, [phase])

  useEffect(() => {
    if (phase === 'running') questionStart.current = performance.now()
  }, [phase, position])

  async function start() {
    setBusy(true)
    setError(null)
    try {
      const session = await api.startQuiz({ level, mode, count, only_due: onlyDue })
      if (!mounted.current) return
      setQuiz(session)
      setAnswers([])
      setPosition(0)
      setResult(null)
      quizStart.current = performance.now()
      setTick(quizStart.current)
      setPhase('running')
    } catch (err) {
      if (mounted.current) setError(errText(err))
    } finally {
      if (mounted.current) setBusy(false)
    }
  }

  async function finish(session: QuizSession, all: Answer[]) {
    setBusy(true)
    setError(null)
    try {
      const marked = await api.submitQuiz({
        quiz_id: session.quiz_id,
        answers: all,
        duration_ms: Math.round(performance.now() - quizStart.current),
      })
      if (!mounted.current) return
      setResult(marked)
      setPhase('done')
    } catch (err) {
      if (mounted.current) setError(errText(err))
    } finally {
      if (mounted.current) setBusy(false)
    }
  }

  async function choose(text: string) {
    if (!quiz || busy) return
    const question = quiz.questions[position]
    const all = [
      ...answers,
      {
        position: question.position,
        answer: text,
        // performance.now() keeps advancing while the tab is backgrounded, so a
        // question left open over a lunch break would exceed the server's 30
        // minute cap and get the whole submission rejected with a 422.
        elapsed_ms: clampElapsed(performance.now() - questionStart.current),
      },
    ]
    setAnswers(all)
    if (all.length < quiz.questions.length) {
      setPosition((p) => p + 1)
      return
    }
    await finish(quiz, all)
  }

  // The options are labelled A-D, so those keys have to actually work.
  const chooseRef = useRef(choose)
  chooseRef.current = choose
  useEffect(() => {
    if (phase !== 'running' || !quiz) return
    const options = quiz.questions[position]?.options
    if (!options) return
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return
      const hit = options.find((o) => o.key.toLowerCase() === e.key.toLowerCase())
      if (!hit) return
      e.preventDefault()
      void chooseRef.current(hit.text)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase, quiz, position])

  // ------------------------------------------------------------------ setup
  if (phase === 'setup') {
    return (
      <div className="mx-auto w-full max-w-2xl space-y-6 px-5 py-8">
        <header className="space-y-1">
          <h1 className="text-2xl font-bold text-slate-100">Quiz</h1>
          <p className="text-sm text-ink-500">
            Four options per question. Answers feed the same review schedule as studying does.
          </p>
        </header>

        <form
          className="card space-y-6 p-6"
          onSubmit={(e) => {
            e.preventDefault()
            void start()
          }}
        >
          <fieldset>
            <legend className="label">Level</legend>
            <LevelPicker value={level} onChange={setLevel} />
          </fieldset>

          <div>
            <label className="label" htmlFor="quiz-mode">
              Question type
            </label>
            <select
              id="quiz-mode"
              className="input"
              value={mode}
              onChange={(e) => setMode(e.target.value as QuizMode)}
            >
              {MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-ink-500">
              {MODES.find((m) => m.value === mode)?.blurb}
            </p>
          </div>

          <fieldset>
            <legend className="label">Questions</legend>
            <div className="flex gap-2">
              {COUNTS.map((n) => (
                <button
                  key={n}
                  type="button"
                  aria-pressed={count === n}
                  onClick={() => setCount(n)}
                  className={`btn flex-1 border ${
                    count === n
                      ? 'border-brand-500/60 bg-brand-500/15 text-brand-500'
                      : 'border-ink-700 bg-ink-850 text-slate-200 hover:border-ink-600'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="flex items-start gap-2.5">
            <input
              id="quiz-only-due"
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-ink-700 bg-ink-950 accent-brand-500"
              checked={onlyDue}
              onChange={(e) => setOnlyDue(e.target.checked)}
            />
            <label htmlFor="quiz-only-due" className="text-sm text-slate-200">
              Only cards that are due
              <span className="block text-xs text-ink-500">
                Falls back to the whole level if there are not enough due cards.
              </span>
            </label>
          </div>

          {error && <ErrorNote>{error}</ErrorNote>}

          <div className="flex items-center gap-3">
            <button type="submit" className="btn-primary" disabled={busy}>
              Start quiz
            </button>
            {busy && <Spinner label="Writing your questions…" />}
          </div>
        </form>
      </div>
    )
  }

  // ---------------------------------------------------------------- running
  if (phase === 'running' && quiz) {
    const question = quiz.questions[position]
    const total = quiz.questions.length
    const jpOptions = JP_ANSWER_MODES.includes(question.prompt_type)
    const awaitingSubmit = answers.length === total

    return (
      <div className="mx-auto w-full max-w-2xl space-y-5 px-5 py-8">
        <header className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-ink-500">
            <LevelBadge level={quiz.level} />
            <span>{MODES.find((m) => m.value === quiz.mode)?.label ?? quiz.mode}</span>
          </div>
          <div className="font-mono text-sm text-ink-500" aria-live="off">
            {clock(tick - quizStart.current)}
          </div>
        </header>

        <div className="space-y-1.5">
          <Progressbar value={answers.length} max={total} color={levelColor[quiz.level]} />
          <div className="text-[11px] text-ink-500">
            Question {Math.min(position + 1, total)} of {total}
          </div>
        </div>

        <section className="card animate-fade-up space-y-6 p-6 sm:p-8">
          <div className="space-y-3 text-center">
            <p className="text-base font-semibold text-slate-100">{question.prompt}</p>
            {question.char !== null && (
              <div className="jp select-none text-[128px] font-semibold leading-none text-slate-100">
                {question.char}
              </div>
            )}
            {question.hint !== null && <p className="text-xs text-ink-500">Hint: {question.hint}</p>}
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {question.options.map((option) => (
              <button
                key={option.key}
                type="button"
                disabled={busy || awaitingSubmit}
                onClick={() => void choose(option.text)}
                className="btn-ghost justify-start px-4 py-3 text-left"
              >
                <span className="mr-1 w-4 shrink-0 text-xs font-bold text-ink-500">{option.key}</span>
                <span className={jpOptions ? 'jp text-2xl' : 'text-sm'}>{option.text}</span>
              </button>
            ))}
          </div>

          {error && <ErrorNote>{error}</ErrorNote>}

          {awaitingSubmit && !busy && (
            <button type="button" className="btn-primary w-full" onClick={() => void finish(quiz, answers)}>
              Submit results
            </button>
          )}
          {busy && (
            <div className="flex justify-center">
              <Spinner label="Marking your answers…" />
            </div>
          )}
        </section>
      </div>
    )
  }

  // ------------------------------------------------------------------- done
  if (phase === 'done' && result) {
    const accuracy = Math.round(result.accuracy * 100)
    return (
      <div className="mx-auto w-full max-w-2xl space-y-5 px-5 py-8">
        <section className="card space-y-5 p-6 text-center sm:p-8">
          <div
            className="mx-auto flex h-24 w-24 items-center justify-center rounded-full text-3xl font-bold"
            style={{
              background: result.passed ? 'rgba(34,197,94,.15)' : 'rgba(244,63,94,.12)',
              color: result.passed ? '#22c55e' : '#fb7185',
            }}
          >
            {accuracy}%
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-100">
              {result.score} of {result.total} correct
            </h1>
            <p className="mt-1 text-sm text-ink-500">
              {result.passed ? 'Passed' : 'Not passed — the mark is 70%'} · finished in{' '}
              {clock(result.duration_ms)}
            </p>
          </div>

          {result.level_up_suggested && (
            <div className="rounded-xl border border-brand-500/40 bg-brand-500/10 px-4 py-3 text-sm text-brand-500">
              That was comfortable. Consider moving your target level up — you can change it on your profile.
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-2">
            <button type="button" className="btn-primary" disabled={busy} onClick={() => void start()}>
              Try another
            </button>
            <button type="button" className="btn-ghost" onClick={() => setPhase('setup')}>
              Change settings
            </button>
            <Link className="btn-ghost" to="/scores">
              View scorecard
            </Link>
          </div>
          {busy && (
            <div className="flex justify-center">
              <Spinner label="Writing your questions…" />
            </div>
          )}
          {error && <ErrorNote>{error}</ErrorNote>}
        </section>

        <QuizReview items={result.items} level={quiz?.level ?? level} />
      </div>
    )
  }

  // Reachable only if a phase transition raced a failed request.
  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 px-5 py-8">
      {error ? <ErrorNote>{error}</ErrorNote> : <Spinner />}
      <button type="button" className="btn-ghost" onClick={() => setPhase('setup')}>
        Back to setup
      </button>
    </div>
  )
}
